import {
  ActionIcon,
  Button,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArrowDown,
  IconDots,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFileDescription,
  IconFileExport,
  IconFolder,
  IconHome,
  IconPalette,
  IconPlus,
  IconSearch,
  IconSettings,
  IconStar,
  IconStarFilled,
  IconTrash,
} from "@tabler/icons-react";
import {
  useSpaceWatchStatusQuery,
  useWatchSpaceMutation,
  useUnwatchSpaceMutation,
} from "@/features/space/queries/space-watcher-query.ts";
import classes from "./space-sidebar.module.css";
import React from "react";
import { useAtom } from "jotai";
import { treeApiAtom } from "@/features/page/tree/atoms/tree-api-atom.ts";
import { setPendingCreateExtra } from "@/features/page/tree/atoms/pending-create-atom.ts";
import { Link, useLocation, useParams } from "react-router-dom";
import clsx from "clsx";
import { useDisclosure } from "@mantine/hooks";
import SpaceSettingsModal from "@/features/space/components/settings-modal.tsx";
import { useGetSpaceBySlugQuery } from "@/features/space/queries/space-query.ts";
import { getSpaceUrl } from "@/lib/config.ts";
import SpaceTree from "@/features/page/tree/components/space-tree.tsx";
import { useSpaceAbility } from "@/features/space/permissions/use-space-ability.ts";
import {
  SpaceCaslAction,
  SpaceCaslSubject,
} from "@/features/space/permissions/permissions.type.ts";
import PageImportModal from "@/features/page/components/page-import-modal.tsx";
import { useTranslation } from "react-i18next";
import { SwitchSpace } from "./switch-space";
import ExportModal from "@/components/common/export-modal";
import {
  useFavoriteIds,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
} from "@/features/favorite/queries/favorite-query";
import { mobileSidebarAtom } from "@/components/layouts/global/hooks/atoms/sidebar-atom.ts";
import { useToggleSidebar } from "@/components/layouts/global/hooks/hooks/use-toggle-sidebar.ts";
import { searchSpotlight } from "@/features/search/constants";
import { TabsStrip } from "@/features/tab/components/tabs-strip.tsx";
import { OpenApiOperationsTree } from "@/features/tab/components/openapi-operations-tree.tsx";
import { activeTabBySpaceAtom } from "@/features/tab/atoms/active-tab-atom.ts";
import { useTabsQuery } from "@/features/tab/queries/tab-query.ts";

export function SpaceSidebar() {
  const { t } = useTranslation();
  const [tree] = useAtom(treeApiAtom);
  const location = useLocation();
  const [opened, { open: openSettings, close: closeSettings }] =
    useDisclosure(false);
  const [mobileSidebarOpened] = useAtom(mobileSidebarAtom);
  const toggleMobileSidebar = useToggleSidebar(mobileSidebarAtom);

  const { spaceSlug } = useParams();
  const { data: space } = useGetSpaceBySlugQuery(spaceSlug);

  const spaceRules = space?.membership?.permissions;
  const spaceAbility = useSpaceAbility(spaceRules);

  // IMPORTANT: all hooks must run unconditionally BEFORE any early return,
  // otherwise React throws error #310 (hook count mismatch between renders).
  // These useState calls used to live after `if (!space) return`, so the
  // first (loading) render had 4 fewer hooks than subsequent ones —
  // crashing the editor into a grey screen on collab reconnects.
  const [linkModalOpen, setLinkModalOpen] = React.useState(false);
  const [linkTitle, setLinkTitle] = React.useState("");
  const [linkUrl, setLinkUrl] = React.useState("");
  const [showHidden, setShowHidden] = React.useState(true);
  const [activeTabBySpace] = useAtom(activeTabBySpaceAtom);
  const { data: tabs = [] } = useTabsQuery(space?.id);
  const activeTabId = space ? activeTabBySpace[space.id] : null;
  const activeTab =
    activeTabId && activeTabId !== "loose"
      ? tabs.find((t) => t.id === activeTabId)
      : null;
  const isOpenApiTab = activeTab?.kind === "openapi";

  if (!space) {
    return <></>;
  }

  function handleCreateRoot(extra?: {
    kind?: "page" | "group" | "link";
    externalUrl?: string;
    title?: string;
  }) {
    // react-arborist v3 drops the `data` arg of tree.create, so we hand the
    // extras to onCreate through a module-level side channel (consumed +
    // cleared there). See pending-create-atom.ts.
    setPendingCreateExtra(extra ?? null);
    tree?.create({
      parentId: null,
      type: "internal",
      index: 0,
    });
  }

  function handleCreatePage() {
    handleCreateRoot({ kind: "page" });
  }

  function submitLink() {
    const url = linkUrl.trim();
    if (!url) return;
    handleCreateRoot({
      kind: "link",
      externalUrl: url,
      title: linkTitle.trim() || url,
    });
    setLinkModalOpen(false);
    setLinkTitle("");
    setLinkUrl("");
  }

  return (
    <>
      <div className={classes.navbar}>
        <div
          className={classes.section}
          style={{
            border: "none",
            marginTop: 2,
            marginBottom: 3,
          }}
        >
          <Group
            gap={4}
            wrap="nowrap"
            justify="space-between"
            style={{ width: "100%" }}
          >
            <SwitchSpace
              spaceName={space?.name}
              spaceSlug={space?.slug}
              spaceIcon={space?.logo}
            />
          </Group>
        </div>

        <div className={classes.section}>
          <div className={classes.menuItems}>
            <UnstyledButton
              component={Link}
              to={getSpaceUrl(spaceSlug)}
              className={clsx(
                classes.menu,
                location.pathname.toLowerCase() === getSpaceUrl(spaceSlug)
                  ? classes.activeButton
                  : "",
              )}
            >
              <div className={classes.menuItemInner}>
                <IconHome
                  size={18}
                  className={classes.menuItemIcon}
                  stroke={2}
                />
                <span>{t("Overview")}</span>
              </div>
            </UnstyledButton>

            <UnstyledButton
              className={classes.menu}
              onClick={searchSpotlight.open}
            >
              <div className={classes.menuItemInner}>
                <IconSearch
                  size={18}
                  className={classes.menuItemIcon}
                  stroke={2}
                />
                <span>{t("Search")}</span>
              </div>
            </UnstyledButton>

            {spaceAbility.can(
              SpaceCaslAction.Manage,
              SpaceCaslSubject.Settings,
            ) && (
              <UnstyledButton className={classes.menu} onClick={openSettings}>
                <div className={classes.menuItemInner}>
                  <IconSettings
                    size={18}
                    className={classes.menuItemIcon}
                    stroke={2}
                  />
                  <span>{t("Space settings")}</span>
                </div>
              </UnstyledButton>
            )}

            {spaceAbility.can(
              SpaceCaslAction.Manage,
              SpaceCaslSubject.Settings,
            ) && (
              <UnstyledButton
                component={Link}
                to={`/settings/spaces/${space.id}/public`}
                className={clsx(
                  classes.menu,
                  location.pathname === `/settings/spaces/${space.id}/public`
                    ? classes.activeButton
                    : "",
                )}
              >
                <div className={classes.menuItemInner}>
                  <IconPalette
                    size={18}
                    className={classes.menuItemIcon}
                    stroke={2}
                  />
                  <span>{t("Customize")}</span>
                </div>
              </UnstyledButton>
            )}

            {spaceAbility.can(
              SpaceCaslAction.Manage,
              SpaceCaslSubject.Page,
            ) && (
              <UnstyledButton
                className={classes.menu}
                onClick={() => {
                  handleCreatePage();
                  if (mobileSidebarOpened) {
                    toggleMobileSidebar();
                  }
                }}
              >
                <div className={classes.menuItemInner}>
                  <IconPlus
                    size={18}
                    className={classes.menuItemIcon}
                    stroke={2}
                  />
                  <span>{t("New page")}</span>
                </div>
              </UnstyledButton>
            )}
          </div>
        </div>

        <div className={clsx(classes.section, classes.sectionPages)}>
          <Group className={classes.pagesHeader} justify="space-between">
            <Text size="xs" fw={500} c="dimmed">
              {t("Pages")}
            </Text>

            <Group gap="xs">
              <Tooltip
                label={
                  showHidden
                    ? t("Hide hidden pages from sidebar")
                    : t("Reveal hidden pages")
                }
                withArrow
                position="top"
              >
                <ActionIcon
                  variant="default"
                  size={18}
                  onClick={() => setShowHidden(!showHidden)}
                  aria-label={
                    showHidden
                      ? t("Hide hidden pages from sidebar")
                      : t("Reveal hidden pages")
                  }
                >
                  {showHidden ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                </ActionIcon>
              </Tooltip>
              <SpaceMenu
                spaceId={space.id}
                canManagePages={spaceAbility.can(
                  SpaceCaslAction.Manage,
                  SpaceCaslSubject.Page,
                )}
                onSpaceSettings={openSettings}
              />

              {spaceAbility.can(
                SpaceCaslAction.Manage,
                SpaceCaslSubject.Page,
              ) && (
                <Menu shadow="md" width={200} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <Tooltip label={t("Create")} withArrow position="right">
                      <ActionIcon
                        variant="default"
                        size={18}
                        aria-label={t("Create")}
                      >
                        <IconPlus />
                      </ActionIcon>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconFileDescription size={16} />}
                      onClick={() => handleCreateRoot({ kind: "page" })}
                    >
                      {t("New page")}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconFolder size={16} />}
                      onClick={() =>
                        handleCreateRoot({
                          kind: "group",
                          title: t("New group"),
                        })
                      }
                    >
                      {t("New group")}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconExternalLink size={16} />}
                      onClick={() => setLinkModalOpen(true)}
                    >
                      {t("External link")}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>

          <TabsStrip
            spaceId={space.id}
            // Tabs are containers for pages — anyone who can manage pages
            // (Writers + Admins + Owners) can create / rename / delete
            // them. Backend mirror: TabController.requireManagePage.
            canManage={spaceAbility.can(
              SpaceCaslAction.Manage,
              SpaceCaslSubject.Page,
            )}
          />

          <div className={classes.pages}>
            {isOpenApiTab && activeTab ? (
              <OpenApiOperationsTree
                tab={activeTab}
                spaceSlug={space.slug}
                customDomain={space.customDomain ?? null}
                canManage={spaceAbility.can(
                  SpaceCaslAction.Manage,
                  SpaceCaslSubject.Settings,
                )}
              />
            ) : (
              <SpaceTree
                spaceId={space.id}
                readOnly={spaceAbility.cannot(
                  SpaceCaslAction.Manage,
                  SpaceCaslSubject.Page,
                )}
                showHidden={showHidden}
                tabId={(activeTabBySpace[space.id] as string | "loose" | null | undefined) ?? undefined}
              />
            )}
          </div>
        </div>
      </div>

      <SpaceSettingsModal
        opened={opened}
        onClose={closeSettings}
        spaceId={space?.slug}
      />

      <Modal
        opened={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        title={t("External link")}
        centered
      >
        <Stack>
          <TextInput
            label={t("Title")}
            placeholder={t("Link title")}
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.currentTarget.value)}
          />
          <TextInput
            label={t("URL")}
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setLinkModalOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={submitLink}>{t("Create")}</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

interface SpaceMenuProps {
  spaceId: string;
  canManagePages: boolean;
  onSpaceSettings: () => void;
}
function SpaceMenu({
  spaceId,
  canManagePages,
  onSpaceSettings,
}: SpaceMenuProps) {
  const { t } = useTranslation();
  const { spaceSlug } = useParams();
  const [importOpened, { open: openImportModal, close: closeImportModal }] =
    useDisclosure(false);
  const [exportOpened, { open: openExportModal, close: closeExportModal }] =
    useDisclosure(false);

  const { data: watchStatus } = useSpaceWatchStatusQuery(spaceId);
  const watchMutation = useWatchSpaceMutation();
  const unwatchMutation = useUnwatchSpaceMutation();
  const isWatching = watchStatus?.watching ?? false;

  const favoriteIds = useFavoriteIds("space");
  const addFavoriteMutation = useAddFavoriteMutation();
  const removeFavoriteMutation = useRemoveFavoriteMutation();
  const isFavorited = favoriteIds.has(spaceId);

  const handleToggleFavorite = () => {
    const params = { type: "space" as const, spaceId };
    if (isFavorited) {
      removeFavoriteMutation.mutate(params);
    } else {
      addFavoriteMutation.mutate(params);
    }
  };

  const handleToggleWatch = () => {
    if (isWatching) {
      unwatchMutation.mutate(spaceId);
    } else {
      watchMutation.mutate(spaceId);
    }
  };

  return (
    <>
      <Menu width={200} shadow="md" withArrow>
        <Menu.Target>
          <Tooltip label={t("Space menu")} withArrow position="top">
            <ActionIcon
              variant="default"
              size={18}
              aria-label={t("Space menu")}
            >
              <IconDots />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            onClick={handleToggleFavorite}
            leftSection={
              isFavorited ? (
                <IconStarFilled
                  size={16}
                  color="var(--mantine-color-yellow-filled)"
                />
              ) : (
                <IconStar size={16} />
              )
            }
          >
            {isFavorited ? t("Remove from favorites") : t("Add to favorites")}
          </Menu.Item>

          <Menu.Item
            onClick={handleToggleWatch}
            leftSection={
              isWatching ? <IconEyeOff size={16} /> : <IconEye size={16} />
            }
          >
            {isWatching ? t("Stop watching space") : t("Watch space")}
          </Menu.Item>

          {canManagePages && (
            <>
              <Menu.Divider />

              <Menu.Item
                onClick={openImportModal}
                leftSection={<IconArrowDown size={16} />}
              >
                {t("Import pages")}
              </Menu.Item>

              <Menu.Item
                onClick={openExportModal}
                leftSection={<IconFileExport size={16} />}
              >
                {t("Export space")}
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                onClick={onSpaceSettings}
                leftSection={<IconSettings size={16} />}
              >
                {t("Space settings")}
              </Menu.Item>

              <Menu.Item
                component={Link}
                to={`/s/${spaceSlug}/trash`}
                leftSection={<IconTrash size={16} />}
              >
                {t("Trash")}
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      {canManagePages && (
        <>
          <PageImportModal
            spaceId={spaceId}
            open={importOpened}
            onClose={closeImportModal}
          />

          <ExportModal
            type="space"
            id={spaceId}
            open={exportOpened}
            onClose={closeExportModal}
          />
        </>
      )}
    </>
  );
}
